import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaFileAlt, FaUpload, FaCheck, FaCheckCircle, FaLock, FaMapMarkerAlt, FaUserFriends, FaUniversity, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { IoDocumentText } from 'react-icons/io5';
import useStepAccessControl from '../../hooks/useStepAccessControl';

/**
 * DataDisplay - Component for rendering banking info or dependents data
 */
const DataDisplay = ({ data, type }) => {
  if (!data) return null;

  if (type === 'banking' && typeof data === 'object' && !Array.isArray(data)) {
    return (
      <div className="mb-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
        <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">Banking Information:</div>
        <div className="space-y-1 text-gray-600 dark:text-gray-400">
          {data.bank_name && <div>Bank: {data.bank_name}</div>}
          {data.account_number && <div>Account: ****{data.account_number.slice(-4)}</div>}
          {data.routing_number && <div>Routing: {data.routing_number}</div>}
        </div>
      </div>
    );
  }

  if (type === 'dependents' && Array.isArray(data)) {
    return (
      <div className="mb-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
        <div className="font-semibold mb-1 text-gray-700 dark:text-gray-300">
          Dependents ({data.length}):
        </div>
        <div className="space-y-1 text-gray-600 dark:text-gray-400">
          {data.map((dependent, idx) => (
            <div key={idx}>
              {idx + 1}: {dependent || `Dependent ${idx + 1}`}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

/**
 * VerticalProgressSteps - A vertical layout progress indicator for tax preparation workflow
 *
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects from API with {label, question, buttons, data}
 * @param {number|boolean} props.currentStep - Current step index or true if all complete
 * @param {number} props.totalSteps - Total number of steps
 * @param {Object} props.user - User object for role-based routing
 * @param {Function} props.onUpdateProfile - Handler for updateProfile button clicks (yes/no)
 * @param {Function} props.onRedirect - Optional handler for redirect button clicks
 * @param {Function} props.onIdUpload - Handler for ID upload button clicks (idType: 'id' or 'spouse_id')
 * @param {boolean} props.inOfficeAppointment - Whether client has in-office appointment (skips document upload requirement)
 */
const VerticalProgressSteps = ({ steps, currentStep, totalSteps, user, onUpdateProfile, onRedirect, onIdUpload, inOfficeAppointment }) => {
  console.log('!!! VERTICAL USER !!!!', user);
  const { canAccessStep, questionnaireRequired, isBusinessAccount } = useStepAccessControl();

  // Collapsed state management - initialize all steps as collapsed except current
  const [collapsedSteps, setCollapsedSteps] = useState(() => {
    const initial = {};
    steps.forEach((_, index) => {
      // Current step is always expanded, all others collapsed
      initial[index] = index !== currentStep;
    });
    return initial;
  });

  // Toggle collapse/expand for a specific step
  const toggleStep = (stepIndex) => {
    setCollapsedSteps(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  // Icon mapping for each step type - extended to support 6 steps
  const getStepIcon = (index) => {
    const iconMap = {
      0: FaMapMarkerAlt,    // Address confirmation
      1: FaUserFriends,     // Dependents
      2: FaUniversity,      // Banking
      3: FaFileAlt,         // Questionnaire
      4: FaUpload,          // Documents
      5: IoDocumentText     // Ready to File
    };
    return iconMap[index] || FaFileAlt;
  };

  // Determine step status
  const getStepStatus = (index) => {
    if (currentStep === true) return 'completed';
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'current';
    return 'pending';
  };

  // Get status-based styling
  const getStatusStyles = (status) => {
    const styles = {
      completed: {
        container: 'bg-green-50 dark:bg-green-900/20 border-green-500',
        icon: 'bg-green-500 text-white',
        text: 'text-green-700 dark:text-green-300',
        button: 'bg-green-600 hover:bg-green-700 text-white'
      },
      current: {
        container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 shadow-md',
        icon: 'bg-blue-500 text-white',
        text: 'text-blue-700 dark:text-blue-300',
        button: 'bg-blue-600 hover:bg-blue-700 text-white'
      },
      pending: {
        container: 'bg-gray-50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-700',
        icon: 'bg-gray-400 dark:bg-gray-600 text-white',
        text: 'text-gray-600 dark:text-gray-400',
        button: 'bg-gray-400 cursor-not-allowed text-white'
      }
    };
    return styles[status] || styles.pending;
  };

  // Render buttons based on button type from API
  const renderButtons = (step, index, status, styles, canAccess, isPending) => {
    const { buttons } = step;

    // No buttons - show "No action required"
    if (!buttons) {
      return (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No action required
        </div>
      );
    }

    const isCompleted = status === 'completed';

    // updateProfile type - Yes/No buttons
    if (buttons.type === 'updateProfile') {
      const { param, options } = buttons;

      return (
        <div className="flex items-center gap-3 flex-wrap">
          {canAccess && !isPending ? (
            <>
              <button
                onClick={(e) => onUpdateProfile && onUpdateProfile(param, options.yes, buttons)}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg
                  text-sm font-medium transition-colors duration-200
                  ${styles.button}
                `}
              >
                Yes ✓
              </button>
              <button
                onClick={(e) => onUpdateProfile && onUpdateProfile(param, options.no, buttons)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 bg-red-600 hover:bg-red-700 text-white"
              >
                No ✕
              </button>
            </>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-400 cursor-not-allowed text-white"
            >
              <FaLock className="text-sm" />
              {isPending ? 'Locked' : 'Complete Previous Steps'}
            </button>
          )}
        </div>
      );
    }

    // redirect type - Custom labeled button with link
    if (buttons.type === 'redirect') {
      const { label, link } = buttons;

      return (
        <div className="flex items-center gap-3 flex-wrap">
          {canAccess && !isPending ? (
            <Link
              to={link}
              onClick={(e) => {
                if (onRedirect) {
                  e.preventDefault();
                  onRedirect(link);
                }
              }}
              className={`
                inline-flex items-center gap-2 px-4 py-2 rounded-lg
                text-sm font-medium transition-colors duration-200
                ${styles.button}
              `}
            >
              {isCompleted ? (
                <>
                  <FaCheck className="text-sm" />
                  <span>View</span>
                </>
              ) : (
                <span>{label || 'Start'}</span>
              )}
            </Link>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-400 cursor-not-allowed text-white"
            >
              <FaLock className="text-sm" />
              {isPending ? 'Locked' : 'Complete Previous Steps'}
            </button>
          )}
        </div>
      );
    }

    // idUpload type - Button to trigger ID upload
    if (buttons.type === 'idUpload') {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => onIdUpload && onIdUpload('id')}
            className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-lg
              text-sm font-medium transition-colors duration-200
              ${styles.button}
            `}
          >
            {buttons.label || 'Upload ID'}
          </button>
        </div>
      );
    }

    // spouseIdUpload type - Button to trigger Spouse ID upload
    if (buttons.type === 'spouseIdUpload') {
      return (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => onIdUpload && onIdUpload('spouse_id')}
            className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-lg
              text-sm font-medium transition-colors duration-200
              ${styles.button}
            `}
          >
            {buttons.label || 'Upload Spouse ID'}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="w-full max-w-full md:max-w-[300px] md:flex-shrink-0 mr-0 md:mr-4 mt-2 mb-4 md:mb-0">

      {/* Overall Progress Summary */}
      <div className="mt-0 mb-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Overall Progress
          </span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {currentStep === true
             ? '100%'
             : `${Math.round((currentStep / totalSteps) * 100)}%`
            }
          </span>
        </div>
        <div className="w-full bg-gray-300 dark:bg-gray-700 rounded-full h-2">
          <div
              className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{
                width: currentStep === true
                       ? '100%'
                       : `${(currentStep / totalSteps) * 100}%`
              }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {steps.map((step, index) => {
          const debug = false;
          const status = getStepStatus(index);
          const styles = getStatusStyles(status);
          const StepIcon = getStepIcon(index);
          const isCompleted = status === 'completed';
          const isCurrent = status === 'current';
          const isPending = status === 'pending';

          // Check if user can access this step
          const canAccess = canAccessStep(index);

          // Special handling for questionnaire step for businesses
          const isQuestionnaireStep = index === 3; // Step 3 in new 6-step format
          const showQuestionnaireOptional = isQuestionnaireStep && isBusinessAccount && !questionnaireRequired;

          // Special handling for document upload step with in-office appointment or drop-off
          const isDocumentStep = index === 4;
          const isDropOff = inOfficeAppointment === 'drop_off';
          const isInOfficeAppt = inOfficeAppointment && !isDropOff;
          const showInOfficeMessage = isDocumentStep && (isDropOff || isInOfficeAppt) && !isBusinessAccount;

          console.log('Step index', index);
          console.log('Is Document Step', isDocumentStep);
          console.log('Show In Office Message', showInOfficeMessage);
          console.log('inOfficeAppt', inOfficeAppointment);
          console.log('isBizAccount', isBusinessAccount);

          const isCollapsed = collapsedSteps[index];

          return (
            <div
              key={index}
              className={`
                relative border-2 rounded-lg transition-all duration-300
                ${styles.container}
                ${isCurrent ? 'scale-[1.02]' : ''}
              `}
            >
              {/* Step Number Badge (top right) */}
              <div className="absolute top-2 right-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                Step {index + 1} of {totalSteps}
              </div>

              {/* Clickable Header */}
              <div
                onClick={() => toggleStep(index)}
                className={`
                  flex items-center gap-4 p-4 cursor-pointer
                  hover:bg-gray-50 dark:hover:bg-gray-800/50
                  transition-colors duration-200
                  ${isCollapsed ? 'rounded-lg' : 'rounded-t-lg'}
                `}
              >
                {/* Icon Container */}
                <div className="relative flex-shrink-0">
                  <div
                    className={`
                      ${isCollapsed ? 'w-10 h-10' : 'w-16 h-16'}
                      rounded-full flex items-center justify-center
                      transition-all duration-300
                      ${styles.icon}
                    `}
                  >
                    <StepIcon className={isCollapsed ? 'text-lg' : 'text-2xl'} />
                  </div>

                  {/* Checkmark Overlay for Completed */}
                  {isCompleted && (
                    <div className="absolute -top-1 -right-1 bg-green-600 rounded-full p-1 shadow-lg">
                      <FaCheckCircle className={`text-white ${isCollapsed ? 'text-sm' : 'text-lg'}`} />
                    </div>
                  )}
                </div>

                {/* Label and Status */}
                <div className="flex-1">
                  <h3 className={`${isCollapsed ? 'text-[12px]' : 'text-lg'} font-semibold ${styles.text} transition-all duration-300`}>
                    {isDocumentStep && isDropOff && !isBusinessAccount
                      ? 'Drop Off Documents'
                      : isDocumentStep && isInOfficeAppt && !isBusinessAccount
                        ? 'Bring Documents'
                        : step.label}
                  </h3>

                  {/* Inline status badges for collapsed view */}
                  {isCollapsed && (
                    <div className="flex items-center gap-2 mt-1">
                      {isCompleted && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                          <FaCheckCircle className="text-xs" />
                          Complete
                        </span>
                      )}
                      {isCurrent && (
                        <span className="inline-flex items-center text-xs text-blue-600 dark:text-blue-400">
                          In Progress
                        </span>
                      )}
                      {isPending && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <FaLock className="text-xs" />
                          Locked
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Chevron Icon */}
                <div className="flex-shrink-0">
                  {isCollapsed ? (
                    <FaChevronDown className={`text-gray-500 dark:text-gray-400 transition-transform duration-300`} />
                  ) : (
                    <FaChevronUp className={`text-gray-500 dark:text-gray-400 transition-transform duration-300`} />
                  )}
                </div>
              </div>

              {/* Collapsible Content */}
              {!isCollapsed && (
                <div className="px-4 pb-4 pt-0">
                  {/* In-office appointment or drop-off message for document step */}
                  {showInOfficeMessage && (
                    <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                        <span className="text-red-500 font-bold">STOP!</span>
                        {isDropOff
                          ? ' If you are using our drop off services, please do not upload any documents to your portal.'
                          : ' If you have an in office appointment, please do not upload any documents to your portal.'
                        }
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                        {isDropOff
                          ? 'Based on your questionnaire, please bring the required documents to our office when dropping off. We will review what you have given to us and let you know if any additional information is needed.'
                          : 'Based on your questionnaire, please bring the required documents to our office for your appointment.'
                        }
                      </p>
                    </div>
                  )}

                  {/* Question Display */}
                  {step.question && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 font-medium">
                      {step.question}
                    </p>
                  )}

                  {/* Data Display (banking or dependents) */}
                  {step.data && (
                    <DataDisplay
                      data={step.data}
                      type={step.dataType || (Array.isArray(step.data) ? 'dependents' : 'banking')}
                    />
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Hide buttons for document step when in-office or drop-off */}
                    {!showInOfficeMessage && renderButtons(step, index, status, styles, canAccess, isPending)}

                    {/* Optional badge for businesses */}
                    {showQuestionnaireOptional && (
                      <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                        Optional for Business
                      </span>
                    )}

                    {/* Status Badge */}
                    {isCompleted && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium">
                        <FaCheckCircle />
                        Complete
                      </span>
                    )}
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                        In Progress
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>


    </div>
  );
};

export default VerticalProgressSteps;
